import { api } from './client'

// === TYPES ===

export interface JiraConfig {
  id: number
  tenant_id: number
  jira_base_url: string
  jira_project_key: string
  jira_secret_arn: string
  sync_enabled: boolean
  poll_interval_seconds: number
  sync_scope: Record<string, unknown>
  created_at: string
  updated_at: string
  // Org-level fields (present when resolved via organization)
  _source?: 'organization'
  org_name?: string
  jira_board_id?: number
  organization_id?: number
}

export interface JiraStatusMapping {
  id: number
  tenant_id: number
  consoleai_status: string
  jira_status_name: string
  jira_status_id: string
  direction: string
}

export interface JiraUserMapping {
  id: number
  tenant_id: number
  consoleai_user_id: number
  jira_account_id: string | null
  jira_display_name: string | null
  match_method: string
}

export interface JiraIssueMirror {
  id: number
  tenant_id: number
  jira_issue_id: string
  jira_issue_key: string
  consoleai_ticket_id: number | null
  summary: string
  status_name: string
  status_category: string
  priority_name: string
  assignee_jira_account_id: string
  issue_type: string
  jira_created_at: string
  jira_updated_at: string
  last_synced_at: string | null
  sync_direction: string | null
  sync_source: string | null
  raw_fields: Record<string, unknown>
}

export interface JiraDashboardSummary {
  project_key: string
  total_issues: number
  by_status_category: Record<string, number>
  by_status: Array<{ name: string; count: number }>
  bugs_open: number
  impediments: number
  recently_updated: JiraIssueMirror[]
  last_sync_at: string | null
}

export interface JiraSyncStatus {
  sync_enabled: boolean
  last_sync_at: string | null
  queue_pending: number
  queue_failed: number
  recent_errors: Array<{
    id: number
    operation: string
    last_error: string
    created_at: string
  }>
}

export interface JiraConflict {
  id: number
  tenant_id: number
  jira_issue_key: string
  field_name: string
  consoleai_value: string | null
  jira_value: string | null
  winner: string
  consoleai_updated_at: string | null
  jira_updated_at: string | null
  resolved_at: string
}

export interface JiraTestResult {
  success: boolean
  project_name?: string
  project_key?: string
  total_issues?: number
  issue_types?: string[]
  error?: string
}

export interface JiraComment {
  id: string
  author_display_name: string
  author_avatar_url: string | null
  body_text: string
  created: string
  updated: string
  is_from_consoleai: boolean
}

export interface JiraSprint {
  id: number
  tenant_id: number
  jira_sprint_id: string
  jira_board_id: string
  name: string
  state: string
  start_date: string | null
  end_date: string | null
  complete_date: string | null
  goal: string | null
  total_story_points: number
  completed_story_points: number
}

export interface JiraVelocityData {
  sprints: Array<{
    name: string
    start_date: string | null
    end_date: string | null
    total_story_points: number
    completed_story_points: number
    completion_rate: number
  }>
  average_velocity: number
  trend: 'improving' | 'stable' | 'declining'
}

export interface JiraCustomField {
  id: string
  name: string
  type: string
  schema: Record<string, unknown> | null
}

export interface JiraCustomFieldMapping {
  jira_field_id: string
  jira_field_name: string
  target: string
  direction: string
}

export interface JiraAttachmentMeta {
  id: string
  filename: string
  mimeType: string | null
  size: number
  author: string
  created: string | null
  content_url: string | null
}

export interface JiraWebhookEvent {
  id: number
  event_type: string
  jira_issue_key: string | null
  processed: boolean
  created_at: string
}

// === API ===

const base = (tenantId: number) => `/v1/tenants/${tenantId}/jira`

export const jiraApi = {
  // Config
  getConfig: (t: number) =>
    api.get<JiraConfig>(`${base(t)}/config`).then(r => r.data),
  upsertConfig: (t: number, data: {
    jira_base_url: string; jira_project_key: string;
    jira_secret_arn: string; poll_interval_seconds?: number
  }) => api.put<JiraConfig>(`${base(t)}/config`, data).then(r => r.data),
  testConnection: (t: number, data: {
    jira_base_url: string; jira_project_key: string; jira_secret_arn: string
  }) => api.post<JiraTestResult>(`${base(t)}/config/test`, data).then(r => r.data),
  toggleSync: (t: number, enabled: boolean) =>
    api.put<JiraConfig>(`${base(t)}/config/toggle`, { enabled }).then(r => r.data),

  // Status Mapping
  getStatusMappings: (t: number) =>
    api.get<{ items: JiraStatusMapping[] }>(`${base(t)}/status-mapping`).then(r => r.data.items),
  upsertStatusMappings: (t: number, mappings: Array<{
    consoleai_status: string; jira_status_name: string;
    jira_status_id: string; direction?: string
  }>) => api.put<{ items: JiraStatusMapping[] }>(`${base(t)}/status-mapping`, { mappings }).then(r => r.data.items),
  getRemoteStatuses: (t: number) =>
    api.get<{ items: unknown[] }>(`${base(t)}/remote-statuses`).then(r => r.data.items),

  // User Mapping
  getUserMappings: (t: number) =>
    api.get<{ items: JiraUserMapping[] }>(`${base(t)}/user-mapping`).then(r => r.data.items),
  autoMatchUsers: (t: number) =>
    api.post<{ matched: number; unmatched: number }>(`${base(t)}/user-mapping/auto`).then(r => r.data),
  updateUserMapping: (t: number, mappingId: number, data: {
    jira_account_id: string; jira_display_name?: string
  }) => api.put<JiraUserMapping>(`${base(t)}/user-mapping/${mappingId}`, data).then(r => r.data),

  // Dashboard
  getSummary: (t: number) =>
    api.get<JiraDashboardSummary>(`${base(t)}/dashboard/summary`).then(r => r.data),
  getIssues: (t: number, params?: {
    status?: string; priority?: string; assignee?: string;
    issue_type?: string; page?: number; page_size?: number
  }) => api.get<{ items: JiraIssueMirror[]; total: number; page: number; page_size: number }>(
    `${base(t)}/dashboard/issues`, { params }
  ).then(r => r.data),
  getIssueDetail: (t: number, jiraKey: string) =>
    api.get<JiraIssueMirror>(`${base(t)}/dashboard/issues/${jiraKey}`).then(r => r.data),
  getIssueComments: (t: number, jiraKey: string) =>
    api.get<{ items: JiraComment[] }>(`${base(t)}/dashboard/issues/${jiraKey}/comments`).then(r => r.data.items),

  // Sync
  linkTicket: (t: number, ticketId: number, jiraKey: string) =>
    api.post<JiraIssueMirror>(`${base(t)}/link/${ticketId}`, null, { params: { jira_key: jiraKey } }).then(r => r.data),
  unlinkTicket: (t: number, ticketId: number) =>
    api.delete(`${base(t)}/link/${ticketId}`).then(r => r.data),
  forceSync: (t: number, ticketId: number) =>
    api.post<JiraIssueMirror>(`${base(t)}/sync/${ticketId}`).then(r => r.data),
  triggerPoll: (t: number) =>
    api.post<{ status: string; tenant_id: number }>(`${base(t)}/sync/trigger`).then(r => r.data),
  getSyncStatus: (t: number) =>
    api.get<JiraSyncStatus>(`${base(t)}/sync/status`).then(r => r.data),

  // Conflicts
  getConflicts: (t: number, limit = 20) =>
    api.get<{ items: JiraConflict[] }>(`${base(t)}/conflicts`, { params: { limit } }).then(r => r.data.items),

  // Custom Fields
  getCustomFields: (t: number) =>
    api.get<{ items: JiraCustomField[] }>(`${base(t)}/custom-fields`).then(r => r.data.items),
  upsertCustomFieldMappings: (t: number, mappings: JiraCustomFieldMapping[]) =>
    api.put<{ items: JiraCustomFieldMapping[] }>(`${base(t)}/custom-field-mappings`, { mappings }).then(r => r.data.items),

  // Sprints
  getSprints: (t: number, state?: string) =>
    api.get<{ items: JiraSprint[] }>(`${base(t)}/sprints`, { params: state ? { state } : {} }).then(r => r.data.items),
  getVelocity: (t: number, limit = 6) =>
    api.get<JiraVelocityData>(`${base(t)}/velocity`, { params: { limit } }).then(r => r.data),

  // Bulk Sync
  bulkSync: (t: number, data: { operation: string; issue_keys: string[]; fields?: Record<string, unknown> }) =>
    api.post<{ queued: number; skipped: number; errors: Array<{ key: string; error: string }> }>(`${base(t)}/bulk-sync`, data).then(r => r.data),

  // Webhook
  registerWebhook: (t: number) =>
    api.post(`${base(t)}/webhook/register`).then(r => r.data),
  deregisterWebhook: (t: number) =>
    api.delete(`${base(t)}/webhook/deregister`).then(r => r.data),
}
